import type { ComponentProps, ComponentType, ReactNode } from "react";
import { useMemo } from "react";
import { isEmpty } from "lodash";

import { Icon } from "orcs-design-system";

import { useLocation, matchPath } from "react-router";
import styled from "styled-components";
import { useFilters } from "src/contexts/filterAndFte/FilterAndFteContext";
import { getInsightsFilterCount } from "src/util/allocationStatus";
import {
  isPeopleSearchPage,
  isSummaryPage,
  isSearchPage,
  isTeamAllocationsPage,
  isStrategyPage,
} from "src/util/url";

import Url, { matchPaths } from "src/consts/urlPaths";

import WorkspaceAnnouncements from "src/components/PersonDetailPage/WorkspaceAnnouncements";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import Restricted from "src/app/Restricted";
import {
  ALLOWED_ROLES,
  getFromUserOrAuth,
  isInternalSupportUser,
} from "src/services/auth";
import { canPerform } from "src/components/Can";
import useGroupsNavigationData from "src/components/GroupsNavigation/useGroupsNavigationData";
import useSidebarFeatureFlags from "./useSidebarFeatureFlags";
import useSidebarGuidedTourState from "./useSidebarGuidedTourState";
import useSidebarUserContext from "./useSidebarUserContext";
import type { Auth0UserOrStub } from "./useSidebarUserContext";
import GroupsNavigationLoader from "src/components/GroupsNavigation";
import TagsNavigation from "src/components/TagsNavigation";
import InviteUser from "src/components/InviteUser";
import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING, EVENT_LOCATIONS } from "src/consts/eventTracking";
import ExploreMenu from "src/components/ExploreMenu";
import {
  isWorkforceHidden,
  isTeamCanvasHidden,
  isPointInTimeHidden,
  getPointInTimeBadge,
  isPageSpecificFilterHidden,
  getFilterBadgeProps,
  getToggleFteBadge,
  isGuidedTourHidden,
} from "./useSideNavItemBuilder.tabVisibility";
import InfoSidebarContent from "./components/InfoSidebarContent";
import AIDashboardsContent from "./components/AIDashboardsContent";
import FilterSidebarContent from "./components/FilterSidebarContent";
import FilterSearchResults from "./components/FilterSearchResults";
import HistoricalDataSidebarContent from "./components/HistoricalDataSidebarContent";
import FteDataSidebarContent from "./components/FteDataSidebarContent";
import SettingsContent from "./components/SettingsContent";
import AppSwitcherContent from "./components/AppSwitcherContent";
import HomeLink from "./components/HomeLink";
import SummaryLink from "./components/SummaryLink";
import PeopleLink from "./components/PeopleLink";
import WorkforceLink from "./components/WorkforceLink";
import DesignsLink from "./components/DesignsLink";
import StrategyInsights from "./components/StrategyInsights";
import TeamCanvasContent from "./components/TeamCanvasContent";

const CustomIcon = styled(Icon)`
  path {
    fill: white !important;
  }
`;

const ITEM_NAMES = {
  HOME: "Home",
  EXPLORE: "Explore",
  BROWSE_TEAMS: "Explore",
  TAGS: "Tags",
  PEOPLE: "People",
  WORKFORCE: "Workforce",
  INSIGHTS: "Insights",
  TEAM_CANVAS: "Team Canvas",
  AI_DASHBOARDS: "Dashboards",
  POINT_IN_TIME: "Point in time",
  APPS: "Apps",
  FILTER: "Filter",
  TEAM_ALIGNMENT: "Team alignment",
  TOGGLE_FTE: "Toggle FTE",
  GUIDED_TOUR: "Guided tour",
  DESIGNS: "Designs",
  SETTINGS: "Settings",
  INVITE: "Invite",
  GET_HELP: "Get help",
  LOGOUT: "Logout",
} as const;

export type SideNavItem = {
  iconName?: string | undefined;
  name: string;
  // These are rendered by an external, untyped SideNav component that
  // injects varying props (item, className, children, etc.) per slot, so a
  // precise prop type isn't feasible here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: ComponentType<any> | (() => ReactNode) | undefined;
  actionType: "link" | "component" | "button";
  isActive?: unknown;
  isExpandedByDefault?: unknown;
  onClick?: ((item?: SideNavItem) => void) | undefined;
  large?: boolean | undefined;
  hide?: boolean | undefined;
  bottomAligned?: boolean | undefined;
  pageSpecific?: boolean | undefined;
  adminOnly?: boolean | undefined;
  badgeNumber?: ReactNode | undefined;
  badgeDot?: boolean | undefined;
};

type UseSideNavItemBuilderParams = {
  clickedItem?: { name?: string } | null;
  user: Auth0UserOrStub;
  tagTypesConfig?: Record<string, unknown>;
  logout?: (() => void) | undefined;
  featureFlags?: unknown;
  eventEmitter?: unknown;
  setClickedItem: (item: SideNavItem) => void;
};

const searchFilterBadge = (type: string | undefined): ReactNode => {
  switch (type) {
    case "all":
      return null;
    case "people":
      return (
        <CustomIcon
          icon={["fas", "user"]}
          title="People"
          size="xs"
          transform="right-1"
        />
      );
    case "positions":
      return (
        <CustomIcon
          icon={["far", "id-badge"]}
          title="Positions"
          size="xs"
          transform="right-1"
        />
      );
    case "teams":
      return (
        <CustomIcon
          icon={["fas", "users"]}
          title="Teams"
          size="xs"
          transform="right-1"
        />
      );
    case "tags":
      return (
        <CustomIcon
          icon={["fas", "tag"]}
          title="Tags"
          size="xs"
          transform="right-1 down-1"
        />
      );
    default:
      return "";
  }
};

const buildExploreTabs = ({
  enableTagExplorer,
  groupsNavProps,
  clickedItem,
  setClickedItem,
}: {
  enableTagExplorer: boolean | undefined;
  groupsNavProps: ReturnType<typeof useGroupsNavigationData>;
  clickedItem?: { name?: string } | null | undefined;
  setClickedItem: (item: SideNavItem) => void;
}): SideNavItem[] => [
  {
    iconName: "layer-group",
    name: ITEM_NAMES.EXPLORE,
    component: () => <ExploreMenu groupsNavProps={groupsNavProps} />,
    onClick: (item) => {
      if (item && clickedItem?.name !== ITEM_NAMES.EXPLORE) {
        setClickedItem(item);
      }
    },
    large: true,
    hide: !enableTagExplorer,
    actionType: "component",
  },
  {
    iconName: "layer-group",
    name: ITEM_NAMES.BROWSE_TEAMS,
    // TODO: useGroupsNavigationData (untyped JS) infers `hierarchyIds`/
    // `setHierarchyIds` as always-null state, which doesn't structurally
    // match GroupsNavigationLoaderProps - pre-existing mismatch between
    // two modules outside this conversion's scope.
    component: () => (
      <GroupsNavigationLoader
        {...(groupsNavProps as unknown as ComponentProps<
          typeof GroupsNavigationLoader
        >)}
      />
    ),
    onClick: (item) => {
      if (item && clickedItem?.name !== ITEM_NAMES.BROWSE_TEAMS) {
        setClickedItem(item);
      }
    },
    large: true,
    hide: enableTagExplorer,
    actionType: "component",
  },
];

const buildFilterTabs = ({
  match,
  isRestricted,
  activeFilterCount,
  insightsFilterCount,
  location,
}: {
  match: ReturnType<typeof matchPaths>;
  isRestricted: boolean | undefined;
  activeFilterCount: number;
  insightsFilterCount: number;
  location: ReturnType<typeof useLocation>;
}): SideNavItem[] => [
  {
    iconName: "filter",
    name: ITEM_NAMES.FILTER,
    hide: isPageSpecificFilterHidden(isSummaryPage(match?.path), isRestricted),
    pageSpecific: true,
    component: () => <FilterSidebarContent />,
    ...getFilterBadgeProps(insightsFilterCount),
    isExpandedByDefault: matchPath(location.pathname, {
      path: Url.INSIGHTS,
    }),
    actionType: "component",
  },
  {
    iconName: "filter",
    isExpandedByDefault: matchPath(location.pathname, {
      path: Url.SEARCH,
      exact: false,
    }),
    // Same label as the legacy /people Filter item (prod).
    name: ITEM_NAMES.FILTER,
    hide: !isSearchPage(match?.path),
    pageSpecific: true,
    // The unified /search/people page reuses the Insights-style active-filter
    // count; every other search type keeps its per-type icon badge.
    ...(match?.params?.["type"] === "people"
      ? getFilterBadgeProps(activeFilterCount)
      : { badgeNumber: searchFilterBadge(match?.params?.["type"]) }),
    // `match` comes from the untyped JS `matchPaths()` (src/consts/urlPaths.js),
    // so it's really `any` here and this cast can't be validated statically.
    // This item is also hidden unless isSearchPage(match?.path) is true, in
    // which case matchPaths() always returns a real FilterSearchResultsMatch
    // shape at runtime - but neither of those are visible to the type checker.
    component: () => (
      <FilterSearchResults
        match={match as ComponentProps<typeof FilterSearchResults>["match"]}
      />
    ),
    actionType: "component",
    isActive: matchPath(location.pathname, {
      path: "/search",
      exact: false,
    }),
  },
];

const useSideNavItemBuilder = ({
  clickedItem,
  user,
  tagTypesConfig,
  logout,
  featureFlags,
  eventEmitter,
  setClickedItem,
}: UseSideNavItemBuilderParams): SideNavItem[] => {
  // Get workspace communications heading
  const workspace = useWorkspace();
  const match = matchPaths();

  const {
    isRestricted,
    isPartiallyRestricted,
    isAdmin,
    defaultRoutePath,
    shouldShowInvite,
  } = useSidebarUserContext({ user, workspace });

  const {
    enableTagExplorer,
    enableAIChat,
    enableOrgDesign,
    enableDemoODOptions,
    enableTeamCanvas,
    enablePositionBackedVacancies,
    enableDesigns,
    dateSettings,
    activeFilterCount,
    fteMode,
  } = useSidebarFeatureFlags();

  const filters = useFilters();
  const insightsFilterCount = getInsightsFilterCount(
    activeFilterCount,
    filters
  );

  const { tourName, triggerGuidedTour, tourAvailable } =
    useSidebarGuidedTourState();

  const userRoles = getFromUserOrAuth(user, "roles", []);
  const showDesigns =
    (canPerform(user, userRoles, ALLOWED_ROLES.DESIGNS) ||
      isInternalSupportUser(user)) &&
    enableDesigns;
  const showWorkforce =
    isAdmin && isInternalSupportUser(user) && !!enablePositionBackedVacancies;

  const { panelHeading, notifications } =
    workspace?.config?.notifications?.landingPageNotifications || {};
  const heading = panelHeading || "Announcements";
  const showDot = !isEmpty(notifications);

  const groupsNavProps = useGroupsNavigationData({
    workspace,
    featureFlags,
    isSelected:
      clickedItem?.name === ITEM_NAMES.BROWSE_TEAMS ||
      clickedItem?.name === ITEM_NAMES.EXPLORE,
    eventEmitter,
  });

  const location = useLocation();

  const sidebarTabs: SideNavItem[] = useMemo(() => {
    return [
      {
        iconName: "home",
        name: ITEM_NAMES.HOME,
        component: HomeLink,
        actionType: "link",
        isActive: !!matchPath(location.pathname, {
          path: defaultRoutePath,
          exact: true,
        }),
        onClick: () => {
          trackEvent(EVENT_TRACKING.URL_CLICKED, {
            action: "home_sidebar_selected",
          });
        },
      },
      ...buildExploreTabs({
        enableTagExplorer,
        groupsNavProps,
        clickedItem,
        setClickedItem,
      }),
      {
        iconName: "tag",
        name: ITEM_NAMES.TAGS,
        component: () => (
          <TagsNavigation
            {...(tagTypesConfig !== undefined ? { tagTypesConfig } : {})}
            selectedTab={
              clickedItem?.name === ITEM_NAMES.TAGS ? "tagsTree" : ""
            }
          />
        ),
        onClick: (item) => {
          if (item && clickedItem?.name !== ITEM_NAMES.TAGS) {
            setClickedItem(item);
          }
        },
        actionType: "component",
      },
      {
        iconName: "users",
        name: ITEM_NAMES.PEOPLE,
        component: PeopleLink,
        actionType: "link",
        hide: isRestricted,
        isActive:
          !!matchPath(location.pathname, {
            path: Url.PEOPLE,
            exact: true,
          }) ||
          isPeopleSearchPage(match) ||
          matchPath<{ type?: string }>(location.pathname, {
            path: Url.SEARCH,
            exact: false,
          })?.params?.type === "people",
      },
      {
        iconName: "clipboard-check",
        name: ITEM_NAMES.WORKFORCE,
        component: WorkforceLink,
        actionType: "link",
        hide: isWorkforceHidden(isRestricted, showWorkforce),
        isActive: !!matchPath(location.pathname, {
          path: Url.WORKFORCE_ROOT,
          exact: false,
        }),
      },
      {
        iconName: "chart-line",
        name: ITEM_NAMES.INSIGHTS,
        component: SummaryLink,
        actionType: "link",
        hide: isRestricted,
        isActive: !!matchPath(location.pathname, {
          path: Url.INSIGHTS,
          exact: true,
        }),
        onClick: () => {
          trackEvent(EVENT_TRACKING.INSIGHTS_CLICKED, {
            location: EVENT_LOCATIONS.NAV_BAR,
          });
        },
      },
      {
        iconName: "sitemap",
        name: ITEM_NAMES.TEAM_CANVAS,
        component: () => <TeamCanvasContent />,
        actionType: "component",
        hide: isTeamCanvasHidden({
          enableOrgDesign,
          enableDemoODOptions,
          enableTeamCanvas,
          isRestricted,
        }),
        isActive: !!matchPath(location.pathname, {
          path: Url.TEAM_CANVAS,
          exact: true,
        }),
      },
      {
        iconName: "sparkles",
        name: ITEM_NAMES.AI_DASHBOARDS,
        component: () => <AIDashboardsContent />,
        actionType: "component",
        hide: !enableAIChat,
      },
      {
        iconName: "history",
        name: ITEM_NAMES.POINT_IN_TIME,
        hide: isPointInTimeHidden(isTeamAllocationsPage(match), isRestricted),
        component: () => (
          <Restricted>
            <HistoricalDataSidebarContent />
          </Restricted>
        ),
        badgeNumber: getPointInTimeBadge(dateSettings),
        actionType: "component",
        bottomAligned: true,
      },
      {
        iconName: "external-link",
        name: ITEM_NAMES.APPS,
        component: () => <AppSwitcherContent />,
        actionType: "component",
        bottomAligned: true,
      },
      ...buildFilterTabs({
        match,
        isRestricted,
        activeFilterCount,
        insightsFilterCount,
        location,
      }),
      {
        iconName: "bolt",
        name: ITEM_NAMES.TEAM_ALIGNMENT,
        isExpandedByDefault: true,
        hide: !isStrategyPage(match?.params),
        pageSpecific: true,
        component: () => (
          <Restricted>
            <StrategyInsights />
          </Restricted>
        ),
        actionType: "component",
      },
      {
        iconName: "people-arrows",
        name: ITEM_NAMES.TOGGLE_FTE,
        hide: isPageSpecificFilterHidden(
          isSummaryPage(match?.path),
          isRestricted
        ),
        pageSpecific: true,
        component: () => (
          <Restricted>
            <FteDataSidebarContent />
          </Restricted>
        ),
        badgeNumber: getToggleFteBadge(fteMode),
        actionType: "component",
      },
      {
        iconName: "play-circle",
        name: ITEM_NAMES.GUIDED_TOUR,
        hide: isGuidedTourHidden({
          isSummaryPage: isSummaryPage(match?.path),
          tourName,
          tourAvailable,
          isRestricted,
        }),
        actionType: "button",
        onClick: () => triggerGuidedTour(tourName),
        adminOnly: true,
        bottomAligned: true,
      },
      {
        iconName: "bell",
        name: heading,
        bottomAligned: true,
        badgeDot: showDot,
        component: () => (
          <Restricted>
            <WorkspaceAnnouncements inSideNav={true} />
          </Restricted>
        ),
        actionType: "component",
        onClick: () => {
          trackEvent(EVENT_TRACKING.URL_CLICKED, {
            action: "announcements_sidebar_selected",
          });
        },
        hide: isRestricted,
      },
      {
        iconName: "project-diagram",
        name: ITEM_NAMES.DESIGNS,
        component: DesignsLink,
        actionType: "link",
        bottomAligned: true,
        isActive: !!matchPath(location.pathname, {
          path: Url.DESIGNS,
          exact: false,
        }),
        hide: !showDesigns,
      },
      {
        iconName: "cog",
        name: ITEM_NAMES.SETTINGS,
        bottomAligned: true,
        component: () => (
          <Restricted>
            <SettingsContent />
          </Restricted>
        ),
        actionType: "component",
        hide: isRestricted || isPartiallyRestricted,
      },
      {
        iconName: "user-plus",
        name: ITEM_NAMES.INVITE,
        hide: !shouldShowInvite,
        large: true,
        bottomAligned: true,
        component: () => <InviteUser workspace={workspace} user={user} />,
        actionType: "component",
      },
      {
        iconName: "question-circle",
        name: ITEM_NAMES.GET_HELP,
        bottomAligned: true,
        component: () => <InfoSidebarContent />,
        actionType: "component",
      },
      {
        iconName: "lock",
        name: ITEM_NAMES.LOGOUT,
        bottomAligned: true,
        onClick: logout,
        actionType: "button",
        hide: !logout,
      },
    ];
  }, [
    activeFilterCount,
    insightsFilterCount,
    dateSettings,
    fteMode,
    groupsNavProps,
    heading,
    isPartiallyRestricted,
    showDesigns,
    showWorkforce,
    user,
    isRestricted,
    logout,
    match,
    setClickedItem,
    shouldShowInvite,
    showDot,
    tagTypesConfig,
    tourAvailable,
    tourName,
    triggerGuidedTour,
    workspace,
    location,
    defaultRoutePath,
    enableTagExplorer,
    enableOrgDesign,
    enableDemoODOptions,
    enableTeamCanvas,
    enableAIChat,
    clickedItem,
  ]);

  return sidebarTabs;
};

export default useSideNavItemBuilder;
